import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { MapPin, TrendingUp, Zap, ArrowRight, Trophy, Shield, Activity, ChevronRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { INDUSTRY_TYPES } from "@/constants/industries";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATE_NAMES = {
  "AL":"Alabama","AK":"Alaska","AZ":"Arizona","AR":"Arkansas","CA":"California",
  "CO":"Colorado","CT":"Connecticut","DE":"Delaware","DC":"D.C.","FL":"Florida",
  "GA":"Georgia","HI":"Hawaii","ID":"Idaho","IL":"Illinois","IN":"Indiana",
  "IA":"Iowa","KS":"Kansas","KY":"Kentucky","LA":"Louisiana","ME":"Maine",
  "MD":"Maryland","MA":"Massachusetts","MI":"Michigan","MN":"Minnesota",
  "MS":"Mississippi","MO":"Missouri","MT":"Montana","NE":"Nebraska","NV":"Nevada",
  "NH":"New Hampshire","NJ":"New Jersey","NM":"New Mexico","NY":"New York",
  "NC":"North Carolina","ND":"North Dakota","OH":"Ohio","OK":"Oklahoma",
  "OR":"Oregon","PA":"Pennsylvania","RI":"Rhode Island","SC":"South Carolina",
  "SD":"South Dakota","TN":"Tennessee","TX":"Texas","UT":"Utah","VT":"Vermont",
  "VA":"Virginia","WA":"Washington","WV":"West Virginia","WI":"Wisconsin","WY":"Wyoming"
};

function IndustryDot({ type }) {
  const ind = INDUSTRY_TYPES.find(i => i.value === type);
  if (!ind) return null;
  const IconComp = ind.icon;
  return (
    <span title={ind.label} className="inline-flex items-center justify-center w-5 h-5 rounded-full flex-shrink-0"
      style={{ background: `${ind.color}20`, border: `1px solid ${ind.color}40` }}>
      {IconComp && <IconComp size={10} style={{ color: ind.color }} />}
    </span>
  );
}

function HeatBar({ count, max }) {
  const pct = Math.min(100, Math.round((count / Math.max(max, 1)) * 100));
  const color = count >= 10 ? "#2ECC71" : count >= 5 ? "#1d4ed8" : count >= 2 ? "#1a3a6c" : "#334155";
  return (
    <div className="flex items-center gap-2 w-full">
      <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
        <div className="h-full rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="text-xs font-black w-8 text-right" style={{ color }}>{count}</span>
    </div>
  );
}

export default function CompetitiveZips() {
  const [data, setData] = useState({ zips: [], total_claimed: 0, top_states: [] });
  const [loading, setLoading] = useState(true);
  const [selectedState, setSelectedState] = useState("ALL");

  useEffect(() => {
    loadData(selectedState);
  }, [selectedState]);

  const loadData = async (state) => {
    setLoading(true);
    try {
      const params = state !== "ALL" ? { state, limit: 50 } : { limit: 50 };
      const res = await axios.get(`${API}/competitive-zips`, { params });
      setData(res.data);
    } catch {}
    setLoading(false);
  };

  const maxCount = data.zips.length > 0 ? data.zips[0].claim_count : 1;

  const rankStyle = (i) => {
    if (i === 0) return { color: "#FFD700", bg: "rgba(255,215,0,0.12)", border: "rgba(255,215,0,0.3)", glow: "0 0 14px rgba(255,215,0,0.3)" };
    if (i === 1) return { color: "#CBD5E1", bg: "rgba(203,213,225,0.08)", border: "rgba(203,213,225,0.2)", glow: "none" };
    if (i === 2) return { color: "#CD7F32", bg: "rgba(205,127,50,0.12)", border: "rgba(205,127,50,0.3)", glow: "0 0 10px rgba(205,127,50,0.2)" };
    return { color: "#475569", bg: "rgba(255,255,255,0.03)", border: "rgba(255,255,255,0.06)", glow: "none" };
  };

  return (
    <div className="min-h-screen" style={{ background: "#060B14" }}>
      <SEOHead
        title="Most Competitive ZIP Codes — Claim Before Your Competitor | NextDayRefund.com"
        description="See which ZIP codes have the most advertisers competing. Claim an exclusive territory before a competitor locks it. 12 industries, all 50 states."
        canonical="/competitive-zips"
      />
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ borderBottom: "1px solid rgba(46,204,113,0.1)" }}>
        <div className="absolute inset-0 map-intel-grid pointer-events-none opacity-40" />
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(244,63,94,0.06) 0%, transparent 70%)" }} />

        <div className="section-container py-14 relative">
          <div className="inline-flex items-center gap-2 mb-5 px-3 py-1.5 rounded-full"
            style={{ background: "rgba(244,63,94,0.1)", border: "1px solid rgba(244,63,94,0.25)" }}>
            <div className="w-2 h-2 rounded-full" style={{ background: "#f43f5e", animation: "livePulse 1.8s ease-in-out infinite" }} />
            <span className="text-xs font-bold tracking-widest uppercase" style={{ color: "#f43f5e" }}>Live Competition Data</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-4 leading-tight">
            Most Competitive<br />
            <span style={{
              background: "linear-gradient(135deg, #f43f5e 0%, #fb923c 50%, #facc15 100%)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text"
            }}>ZIP Code Report</span>
          </h1>
          <p className="text-slate-400 max-w-2xl text-base leading-relaxed mb-8">
            These are the most sought-after territories on NextDayRefund.com — ranked by total advertiser competition.
            Every ZIP below has businesses <strong className="text-white">actively competing</strong> for local customers.
            If your ZIP appears here, it's heating up. <strong className="text-[#2ECC71]">Claim before it's gone.</strong>
          </p>

          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-3 px-5 py-3 rounded-xl"
              style={{ background: "rgba(244,63,94,0.08)", border: "1px solid rgba(244,63,94,0.2)" }}>
              <TrendingUp size={18} className="text-rose-400" />
              <div>
                <div className="text-2xl font-black text-white">{data.total_claimed.toLocaleString()}</div>
                <div className="text-xs text-slate-500 uppercase tracking-wide">Total Active Territories</div>
              </div>
            </div>
            <div className="flex items-center gap-3 px-5 py-3 rounded-xl"
              style={{ background: "rgba(14,22,38,0.85)", border: "1px solid rgba(255,255,255,0.07)" }}>
              <MapPin size={18} className="text-slate-400" />
              <div>
                <div className="text-2xl font-black text-white">{data.zips.length}</div>
                <div className="text-xs text-slate-500 uppercase tracking-wide">Contested ZIPs Shown</div>
              </div>
            </div>
            <Link to="/claim"
              className="flex items-center gap-2 font-black px-6 py-3 rounded-xl text-sm transition-all hover:scale-105"
              style={{ background: "linear-gradient(135deg, #2ECC71, #16a34a)", color: "white", boxShadow: "0 4px 20px rgba(46,204,113,0.3)" }}
              data-testid="hero-claim-cta">
              <Zap size={14} /> Claim a ZIP — $150 <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* State Filter */}
      <section style={{ background: "rgba(8,12,22,0.99)", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
        <div className="section-container py-4">
          <div className="flex items-center gap-3 overflow-x-auto scrollbar-hide">
            <span className="text-slate-500 text-xs font-bold uppercase tracking-widest whitespace-nowrap flex-shrink-0 flex items-center gap-1.5">
              <Activity size={11} className="text-rose-400" /> Filter by State:
            </span>
            <button
              onClick={() => setSelectedState("ALL")}
              data-testid="state-filter-all"
              className="flex-shrink-0 text-xs font-bold px-3 py-1.5 rounded-full transition-all"
              style={selectedState === "ALL"
                ? { background: "linear-gradient(135deg, #f43f5e, #c2410c)", color: "white", boxShadow: "0 2px 10px rgba(244,63,94,0.3)" }
                : { background: "rgba(30,42,62,0.8)", color: "#94a3b8", border: "1px solid rgba(255,255,255,0.07)" }}>
              All States
            </button>
            {data.top_states.map(({ state, count }) => (
              <button key={state}
                onClick={() => setSelectedState(state)}
                data-testid={`state-filter-${state}`}
                className="flex-shrink-0 flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full transition-all whitespace-nowrap"
                style={selectedState === state
                  ? { background: "linear-gradient(135deg, #f43f5e, #c2410c)", color: "white", boxShadow: "0 2px 10px rgba(244,63,94,0.3)" }
                  : { background: "rgba(30,42,62,0.8)", color: "#94a3b8", border: "1px solid rgba(255,255,255,0.07)" }}>
                <MapPin size={9} />
                {state}
                <span className="font-black opacity-70">{count}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Main content */}
      <section className="py-10">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Left: ZIP Rankings */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-5">
                <Trophy size={16} className="text-[#FFD700]" />
                <h2 className="text-white font-bold text-lg">
                  {selectedState !== "ALL" ? `Top ZIPs in ${STATE_NAMES[selectedState] || selectedState}` : "Top Contested ZIP Codes"}
                </h2>
                {loading && <div className="w-4 h-4 border-2 border-rose-400 border-t-transparent rounded-full animate-spin" />}
              </div>

              {!loading && data.zips.length === 0 && (
                <div className="text-center py-16 rounded-2xl"
                  style={{ border: "1px dashed rgba(255,255,255,0.08)", background: "rgba(8,12,20,0.5)" }}>
                  <MapPin size={32} className="text-slate-500 mx-auto mb-3" />
                  <p className="text-slate-300 font-bold">No claimed ZIPs in this area yet</p>
                  <p className="text-slate-500 text-sm mt-1 mb-5">Be the first to own an exclusive territory!</p>
                  <Link to="/claim" className="inline-flex items-center gap-2 font-bold px-6 py-3 rounded-xl text-sm"
                    style={{ background: "linear-gradient(135deg, #2ECC71, #16a34a)", color: "white" }}>
                    Claim a ZIP <ArrowRight size={14} />
                  </Link>
                </div>
              )}

              <div className="space-y-2">
                {data.zips.map((zip, i) => {
                  const rs = rankStyle(i);
                  return (
                    <div key={zip.zip_code}
                      data-testid={`zip-row-${zip.zip_code}`}
                      className="group relative rounded-xl p-4 transition-all duration-200"
                      style={{ background: rs.bg, border: `1px solid ${rs.border}`, boxShadow: rs.glow }}>
                      <div className="flex items-center gap-4">
                        {/* Rank */}
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-black flex-shrink-0"
                          style={{ color: rs.color, border: `1.5px solid ${rs.border}` }}>
                          {i + 1}
                        </div>

                        {/* ZIP + Location */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 flex-wrap mb-1">
                            <span className="text-xl font-black text-white">{zip.zip_code}</span>
                            <span className="text-slate-400 text-sm">
                              {zip.city && zip.state ? `${zip.city}, ${zip.state}` : zip.state || ""}
                            </span>
                          </div>
                          <HeatBar count={zip.claim_count} max={maxCount} />
                          {/* Industry dots */}
                          <div className="flex gap-1 mt-2 flex-wrap">
                            {zip.industries.slice(0, 8).map(ind => (
                              <IndustryDot key={ind} type={ind} />
                            ))}
                            {zip.industries.length > 8 && (
                              <span className="text-xs text-slate-500">+{zip.industries.length - 8}</span>
                            )}
                          </div>
                        </div>

                        {/* Claim CTA */}
                        <div className="flex-shrink-0">
                          <Link to={`/claim`}
                            className="group-hover:flex hidden items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg transition-all"
                            style={{ background: "linear-gradient(135deg, #2ECC71, #16a34a)", color: "white" }}>
                            Claim <ArrowRight size={11} />
                          </Link>
                          <div className="group-hover:hidden flex items-center gap-1 text-xs font-semibold"
                            style={{ color: rs.color }}>
                            {zip.industries.length} industr{zip.industries.length === 1 ? "y" : "ies"}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right: Sidebar */}
            <div className="space-y-5">

              {/* FOMO Card */}
              <div className="rounded-2xl p-6 relative overflow-hidden"
                style={{ background: "rgba(10,15,26,0.95)", border: "1px solid rgba(244,63,94,0.2)" }}>
                <div className="absolute top-0 left-0 right-0 h-px"
                  style={{ background: "linear-gradient(90deg, #f43f5e, #fb923c, transparent)" }} />
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 rounded-full bg-rose-500" style={{ animation: "livePulse 1.5s infinite" }} />
                  <span className="text-rose-400 text-xs font-bold uppercase tracking-widest">FOMO Alert</span>
                </div>
                <h3 className="text-white font-black text-lg mb-2">Your competitor may already be watching</h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-4">
                  Every ZIP above has at least one business already advertising. Once an industry is claimed in your ZIP, <strong className="text-white">it's locked for the entire season.</strong>
                </p>
                <Link to="/claim"
                  className="flex items-center justify-center gap-2 w-full font-black py-3 rounded-xl text-sm transition-all hover:brightness-110"
                  style={{ background: "linear-gradient(135deg, #f43f5e, #c2410c)", color: "white", boxShadow: "0 4px 20px rgba(244,63,94,0.3)" }}
                  data-testid="sidebar-claim-cta">
                  <Zap size={14} /> Check My ZIP Now
                </Link>
              </div>

              {/* Top States */}
              {data.top_states.length > 0 && (
                <div className="rounded-2xl p-5"
                  style={{ background: "rgba(10,15,26,0.95)", border: "1px solid rgba(255,255,255,0.07)" }}>
                  <div className="flex items-center gap-2 mb-4">
                    <Activity size={14} className="text-[#2ECC71]" />
                    <h3 className="text-white font-bold text-sm">Most Active States</h3>
                  </div>
                  <div className="space-y-2">
                    {data.top_states.slice(0, 8).map(({ state, count }, i) => (
                      <button key={state}
                        onClick={() => setSelectedState(state)}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg group transition-all"
                        style={{ background: "rgba(8,12,20,0.7)", border: "1px solid rgba(255,255,255,0.05)" }}>
                        <span className="text-slate-500 text-xs font-bold w-4">{i + 1}</span>
                        <span className="flex-1 text-left text-sm font-semibold text-slate-300 group-hover:text-white transition-colors">
                          {STATE_NAMES[state] || state}
                        </span>
                        <span className="text-xs font-black" style={{
                          background: "linear-gradient(135deg, #2ECC71, #06b6d4)",
                          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text"
                        }}>{count}</span>
                        <ChevronRight size={12} className="text-slate-600 group-hover:text-slate-400" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Industry Legend */}
              <div className="rounded-2xl p-5"
                style={{ background: "rgba(10,15,26,0.95)", border: "1px solid rgba(255,255,255,0.07)" }}>
                <div className="flex items-center gap-2 mb-4">
                  <Shield size={14} className="text-slate-400" />
                  <h3 className="text-white font-bold text-sm">12 Industry Types</h3>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {INDUSTRY_TYPES.filter(t => t.value !== "all").map(ind => {
                    const IconComp = ind.icon;
                    return (
                      <div key={ind.value} title={ind.label}
                        className="flex items-center gap-1.5 px-2 py-1 rounded-full text-xs"
                        style={{ background: `${ind.color}12`, border: `1px solid ${ind.color}25`, color: ind.color }}>
                        {IconComp && <IconComp size={9} />}
                        <span className="hidden sm:inline">{ind.label}</span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-slate-500 text-xs mt-3">One exclusive advertiser per industry per ZIP — $150/year</p>
              </div>

              {/* CTA */}
              <div className="rounded-2xl p-6 text-center relative overflow-hidden"
                style={{ background: "rgba(8,12,20,0.98)", border: "1px solid rgba(46,204,113,0.15)" }}>
                <div className="absolute inset-0 map-intel-grid opacity-30 pointer-events-none" />
                <div className="relative">
                  <div className="text-xs font-bold uppercase tracking-widest text-[#2ECC71] mb-2">Open Territory</div>
                  <h3 className="text-white font-black text-base mb-2">Your ZIP May Still Be Open</h3>
                  <p className="text-slate-500 text-xs mb-4">Check if your area is still available before a competitor does.</p>
                  <Link to="/claim"
                    className="inline-flex items-center gap-2 font-black px-6 py-3 rounded-xl text-sm transition-all hover:brightness-110"
                    style={{ background: "linear-gradient(135deg, #2ECC71, #16a34a)", color: "white", boxShadow: "0 4px 16px rgba(46,204,113,0.3)" }}>
                    Check My ZIP <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
